///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// "Has this happened here before?" Pulls the historical catalog around a
/// mainshock out of the local earthquake_history table: the events that
/// fall inside the cutout footprint (drawn as ghost hypocenters) and a few
/// facts for the panel from everything within 100 km.
///

import { QuakeHistoryTable } from "@/earthbrowser/dataset-distribution/earthquakes/history/QuakeHistoryTable"
import { fromBlockXZ, insideFootprint, localOffsetKm, toBlockXZ, type CutoutLayout, type SequenceEvent } from "./cutoutLayout"
import { bValue } from "./sequenceStats"

const cKmPerDegree = 111.32
const cFactsRadiusKm = 100
/** History older than this is never in the live 30-day table, so no double counting */
const cLiveWindowMs = 35 * 86400000

export interface QuakeHistoryFacts {
	/** Catalog floor magnitude (what "events" means in the counts) */
	minMag: number
	/** First year with coverage in the catalog */
	sinceYear: number
	/** M≥minMag events within cFactsRadiusKm of the epicenter */
	countNearby: number
	radiusKm: number
	largest?: { mag: number; year: number; distanceKm: number; depthKm: number }
	lastM6Year?: number
	lastM7Year?: number
	/** Events inside the footprint but below the box floor (not drawn) */
	hiddenDeeper: number
	/** Regional Gutenberg-Richter b-value from the catalog within the radius (Mc = catalog floor) */
	regionalB?: { b: number; stdErr: number; n: number }
}

export interface QuakeHistory {
	/** Events inside the footprint and above the floor, as sequence events (inSequence false) */
	ghosts: SequenceEvent[]
	facts: QuakeHistoryFacts
}

export class QuakeHistoryQuery {
	/** Undefined when the catalog table has no rows yet (first sync still running) */
	public static async load(mainshockLat: number, mainshockLon: number, layout: CutoutLayout, liveIds: Set<string>): Promise<QuakeHistory | undefined> {
		if ((await QuakeHistoryTable.getCount()) === 0) return undefined

		// One box covering both the rotated footprint and the facts radius
		let halfEast = cFactsRadiusKm
		let halfNorth = cFactsRadiusKm
		for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
			const c = fromBlockXZ(sx * layout.lengthKm / 2, sz * layout.widthKm / 2, layout.strike)
			halfEast = Math.max(halfEast, Math.abs(c.east + layout.centerEast))
			halfNorth = Math.max(halfNorth, Math.abs(c.north + layout.centerNorth))
		}
		const dLat = halfNorth / cKmPerDegree
		const dLon = halfEast / (cKmPerDegree * Math.max(Math.cos(mainshockLat * Math.PI / 180), 0.05))
		const beforeMs = Date.now() - cLiveWindowMs
		const rows = await QuakeHistoryTable.queryBox(mainshockLat - dLat, mainshockLat + dLat, mainshockLon - dLon, mainshockLon + dLon, beforeMs)

		const ghosts: SequenceEvent[] = []
		const nearbyMags: number[] = []
		const facts: QuakeHistoryFacts = {
			minMag: 4.5,
			sinceYear: 1900,
			countNearby: 0,
			radiusKm: cFactsRadiusKm,
			hiddenDeeper: 0
		}
		for (const r of rows) {
			if (liveIds.has(r.id)) continue
			const local = localOffsetKm(r.lat, r.lon, mainshockLat, mainshockLon)
			const distance = Math.hypot(local.east, local.north)
			const year = new Date(r.time_ms).getUTCFullYear()

			if (distance <= cFactsRadiusKm) {
				facts.countNearby++
				nearbyMags.push(r.mag)
				if (!facts.largest || r.mag > facts.largest.mag) {
					facts.largest = { mag: r.mag, year, distanceKm: distance, depthKm: r.depth_km }
				}
				if (r.mag >= 6 && (facts.lastM6Year === undefined || year > facts.lastM6Year)) facts.lastM6Year = year
				if (r.mag >= 7 && (facts.lastM7Year === undefined || year > facts.lastM7Year)) facts.lastM7Year = year
			}

			const xz = toBlockXZ(local.east - layout.centerEast, local.north - layout.centerNorth, layout.strike)
			if (!insideFootprint(layout, xz.x, xz.z)) continue
			if (r.depth_km > layout.depthFloorKm) {
				facts.hiddenDeeper++
				continue
			}
			ghosts.push({
				id: r.id,
				lat: r.lat,
				lon: r.lon,
				depthKm: r.depth_km,
				mag: r.mag,
				timeMs: r.time_ms,
				inSequence: false,
				east: local.east,
				north: local.north
			})
		}
		// Regional b-value baseline: the catalog is complete to its floor
		// everywhere, so Mc is the floor itself; needs a few dozen events
		if (nearbyMags.length >= 30) {
			const regional = bValue(nearbyMags, facts.minMag)
			if (regional) facts.regionalB = { b: regional.b, stdErr: regional.stdErr, n: regional.n }
		}
		return { ghosts, facts }
	}
}
